import { get } from './api-client';

export interface CampaignInfoResponse {
  promotional_image: string;
  copywriting_title: string;
  copywriting_subtitle: string;
  copywriting_description: string;
}

export const getCampaignInfo = async (
  templateIdentifier: string
): Promise<CampaignInfoResponse> => {
  return get<CampaignInfoResponse>(`/campaigns/${encodeURIComponent(templateIdentifier)}/info`);
};

interface PaginatedResponse<T> {
  items: T[];
  total: number;
}

export type CampaignTemplateType = 'image' | 'html' | 'text';

export interface ResellerCampaign {
  id: string;
  title: string;
  description: string;
  target_audience: string;
  started_chats: number;
  leads_count: number;
  requested_quotes: number;
  created_at: string;
}

export interface ResellerCampaignDetails extends ResellerCampaign {
  template_id: string;
  url: string;
  reseller_id: string;
  reseller_name: string;
  reseller_email: string;
  content: string;
  template_type: CampaignTemplateType;
}

export interface ResellerCampaignChat {
  id: string;
  campaign_id: string | null;
  identification?: string | null;
  is_lead?: boolean;
  request_quote?: boolean;
  messages: number | null;
  follow_up_provided?: boolean | null;
  has_new_messages?: boolean;
  created_at: string;
}

export interface ResellerCampaignChatMessage {
  id: string;
  message: string;
  role: string;
  created_at: string;
}

export interface ResellerCampaignChatProductInfo {
  id: string;
  name: string;
  sku: string;
  image: string;
  quantity?: number | null;
}

export interface ResellerCampaignChatDetails {
  id: string;
  messages: ResellerCampaignChatMessage[];
  identification?: string | null;
  is_lead?: boolean;
  request_quote?: boolean;
  cnpj?: string | null;
  contact?: string | null;
  address?: string | null;
  budget?: string | null;
  authority?: string | null;
  need?: string | null;
  timeframe?: string | null;
  products?: ResellerCampaignChatProductInfo[] | null;
  created_at: string;
  updated_at?: string | null;
  has_new_messages?: boolean;
  follow_up_provided?: boolean | null;
}

export const getResellerCampaigns = async (
  skip = 0,
  limit = 10
): Promise<PaginatedResponse<ResellerCampaign> | null> => {
  try {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
      sort: 'created_at:desc',
    });

    return await get<PaginatedResponse<ResellerCampaign>>(`/campaigns/user?${params.toString()}`);
  } catch (error) {
    console.error('Error fetching reseller campaigns:', error);
    return null;
  }
};

export const getResellerCampaignDetails = async (
  campaignId: string
): Promise<ResellerCampaignDetails | null> => {
  try {
    return await get<ResellerCampaignDetails>(`/campaigns/${encodeURIComponent(campaignId)}`);
  } catch (error) {
    console.error('Error fetching reseller campaign details:', error);
    return null;
  }
};

export const getResellerCampaignChats = async (
  campaignId: string,
  skip = 0,
  limit = 10
): Promise<PaginatedResponse<ResellerCampaignChat> | null> => {
  try {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
      sort: 'created_at:desc',
      campaign_id: campaignId,
    });

    return await get<PaginatedResponse<ResellerCampaignChat>>(
      `/campaigns/chats?${params.toString()}`
    );
  } catch (error) {
    console.error('Error fetching reseller campaign chats:', error);
    return null;
  }
};

export const getResellerCampaignChatDetails = async (
  chatId: string
): Promise<ResellerCampaignChatDetails | null> => {
  try {
    return await get<ResellerCampaignChatDetails>(`/campaigns/chats/${encodeURIComponent(chatId)}`);
  } catch (error) {
    console.error('Error fetching reseller campaign chat details:', error);
    return null;
  }
};

export const getCampaignChatHistory = async (
  chatId: string
): Promise<ResellerCampaignChatMessage[] | null> => {
  try {
    return await get<ResellerCampaignChatMessage[]>(
      `/campaigns/chats/${encodeURIComponent(chatId)}/history`
    );
  } catch (error) {
    console.error('Error fetching campaign chat history:', error);
    return null;
  }
};

export const markResellerCampaignChatAsRead = async (chatId: string): Promise<boolean | null> => {
  try {
    return await get<boolean>(`/campaigns/chats/${encodeURIComponent(chatId)}/mark-as-read`);
  } catch (error) {
    console.error('Error marking campaign chat as read:', error);
    return null;
  }
};
